import { useState } from 'react';
import { createRoute, useParams } from '@tanstack/react-router';
import { Shell } from '../components/layout/Shell';
import { PublicShell } from '../components/layout/PublicShell';
import { PublicProfileHeader } from '../components/community/PublicProfileHeader';
import { ProfileItemsGrid } from '../components/community/ProfileItemsGrid';
import { WantedItemCard } from '../components/community/WantedItemCard';
import { CatalogCTA } from '../components/community/CatalogCTA';
import { usePublicProfile, useProfileWanted } from '../hooks/useCommunity';
import { useAuth } from '../hooks/useAuth';
import { useTranslation } from '../i18n';
import { Route as rootRoute } from './__root';
import styles from './profile.$slug.module.css';

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/profile/$slug',
  component: ProfilePage,
});

function ProfilePage() {
  const { slug } = useParams({ from: '/profile/$slug' });
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();
  const { data: profile, isLoading } = usePublicProfile(slug);
  const { data: wantedData } = useProfileWanted(slug);
  const [activeVaultId, setActiveVaultId] = useState<string | null>(null);

  const Wrapper = isAuthenticated ? Shell : PublicShell;

  if (isLoading) {
    return (
      <Wrapper>
        <div className={styles.page}>
          <div>{t('community.loadingProfile')}</div>
        </div>
      </Wrapper>
    );
  }

  if (!profile) {
    return (
      <Wrapper>
        <div className={styles.page}>
          <div className={styles.notFound}>{t('community.profileNotFound')}</div>
        </div>
      </Wrapper>
    );
  }

  const wantedItems = wantedData?.data ?? [];
  const publicVaults = profile.publicVaults ?? [];
  const showVaultTabs = publicVaults.length > 1;

  return (
    <Wrapper>
      <div className={styles.page}>
        <PublicProfileHeader profile={profile} />

        {showVaultTabs && (
          <div className={styles.vaultTabBar}>
            <button
              className={`${styles.vaultTab} ${activeVaultId === null ? styles.vaultTabActive : ''}`}
              onClick={() => setActiveVaultId(null)}
            >
              {t('share.allVaults')}
            </button>
            {publicVaults.map((v) => (
              <button
                key={v.id}
                className={`${styles.vaultTab} ${activeVaultId === v.id ? styles.vaultTabActive : ''}`}
                onClick={() => setActiveVaultId(v.id)}
              >
                {v.name} ({v.itemCount})
              </button>
            ))}
          </div>
        )}

        {publicVaults.length === 0 ? (
          <div className={styles.noPublicVaults}>{t('share.noPublicVaults')}</div>
        ) : (
          <ProfileItemsGrid slug={slug} vaultId={activeVaultId ?? undefined} />
        )}

        {isAuthenticated && wantedItems.length > 0 && (
          <div className={styles.wantedSection}>
            <h2 className={styles.sectionTitle}>{t('community.wantedList')}</h2>
            <div className={styles.wantedGrid}>
              {wantedItems.map((item) => (
                <WantedItemCard key={item.id} item={item} />
              ))}
            </div>
          </div>
        )}

        {!isAuthenticated && <CatalogCTA slug={slug} />}
      </div>
    </Wrapper>
  );
}
